/**
 * 现象编排 —— 对应技术设计 §9「现象由状态驱动」。
 *
 * 【这个文件存在的直接原因】
 * `bug/023-化学/002-bug.md` 暴露了一个系统性问题：实验包用 `{"animate": "flame"}`
 * 请求了 6 段现象，引擎**没有播放器**，`app.ts` 还把这些事件直接丢弃 ——
 * 于是「点燃酒精灯」只改了一个字段，画面上毫无变化，学生以为没反应。
 *
 * 更糟的是**没有任何机制会发现这件事**：包里写了引擎不认识的 animate id，
 * 静默无声。跟 `knowledge.json` 空壳、`showGhost` 无消费方是同一个失败模式。
 *
 * 所以这里做两件事：
 *   ① 声明引擎**实现了哪些现象**（`SUPPORTED_PHENOMENA`）
 *   ② 提供校验：包里请求了未实现的现象 → 加载期报错，不再静默
 *
 * 现象本身怎么画：绝大多数由 `Shape` 的状态求值直接完成（火焰随 `lit`、
 * 液面随 `gasVolumeRatio`），所以这一层只负责**登记与校验**，不重复画一遍。
 * 需要独立时间轴的粒子效果（气泡流、倒吸）留给 canvas-particles，尚未实现。
 */

/** 引擎当前真正能表现出来的现象。**加一个就要真的能画出来**，不许占位。 */
export const SUPPORTED_PHENOMENA = {
  /** 酒精灯火焰：由 AlcoholLamp 的 `flame` 图元随 `state.lit` 绘制 */
  flame: "state-shape",
  /** 试管内药品：随 `state.hasReagent` / `state.solidColor` */
  reagent_solid: "state-shape",
  /** 管口棉花：随 `state.hasCotton` */
  cotton_plug: "state-shape",
  /** 试管受热发红：随 `state.temperatureC` */
  hot_glow: "state-shape",
  /** 集气瓶排水：液面随 `state.gasVolumeRatio` 下降 */
  water_displacement: "state-shape",
  /** 集满后瓶内透亮 */
  bottle_full: "state-shape",
  /** 水中气泡上浮：随水槽 `state.bubbling`。气密性检查与排水集气共用 */
  gas_bubbles: "state-shape",
} as const;

export type PhenomenonId = keyof typeof SUPPORTED_PHENOMENA;

export function isSupportedPhenomenon(id: string): id is PhenomenonId {
  return Object.prototype.hasOwnProperty.call(SUPPORTED_PHENOMENA, id);
}

export function listSupportedPhenomena(): string[] {
  return Object.keys(SUPPORTED_PHENOMENA);
}

/**
 * 找出实验包请求了、但引擎没有实现的现象。
 *
 * 返回非空 = 包与引擎能力对不上账。调用方**必须报错**而不是忽略：
 * 静默忽略正是 bug/023 第 3 条的成因。
 */
export function findUnsupportedPhenomena(requested: readonly string[]): string[] {
  return [...new Set(requested)].filter((id) => !isSupportedPhenomenon(id));
}
